import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HerefordshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Herefordshire Pothole Claims | B/C Roads in RED Doubled | Fixtyer"
        description="Herefordshire Council filled 106,650 potholes in four years on a 3,338km rural network, yet B and C roads in RED condition doubled to 13% by 2024. See what the DfT AMBER rating means for your claim."
        canonical="/councils/herefordshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Herefordshire', url: '/councils/herefordshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Herefordshire earns a GREEN Spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend rating is GREEN because Herefordshire is projecting £50.87m total highway expenditure in 2025/26 against £26.859m DfT grant funding — nearly double. But your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58, not on aggregate spend. Condition is AMBER overall, with B and C roads in RED condition doubling from 6–8% to 13% by 2024.',
          },
          {
            question: 'What if my pothole was on an unclassified or C road?',
            answer: 'Unclassified roads make up 1,566km — 47% of Herefordshire\'s 3,338km network — and 20% were in RED condition at the 2024 survey. C roads are surveyed on a three-year cycle, while U-roads use Vaisala Road AI on a two-year cycle (50% surveyed annually). On a network the council describes as more than 80% rural, with limited transport alternatives, defects on minor roads are common — and prior reports plus photos showing defect age matter more than headline A-road scores.',
          },
          {
            question: 'Does the council\'s £50m investment mean my claim will fail?',
            answer: 'Not automatically. Herefordshire\'s own June 2025 press release states the transparency report "does not reflect the full benefit and impact of the council\'s additional investment over the past two years due to a time lag in national road condition surveys." The published condition tables carry the same caveat. A council cannot rely on investment not yet visible in its own survey data to defeat a claim about a specific defect it failed to repair in time.',
          },
          {
            question: 'Pothole repairs fell to 22,227 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Herefordshire still filled 22,227 potholes in 2024/25 — about 61 every day — after a peak of 31,963 in 2023/24. The council estimates it will repair in excess of 25,000 potholes in 2025/26 while pursuing a preventative strategy. Around 80% of reactive investment goes on carriageway repairs, with the majority being pothole repairs. Fewer fills in one year does not mean fewer defects forming.',
          },
          {
            question: 'Does the time lag in condition surveys help my claim?',
            answer: 'It can. Every condition table in Herefordshire\'s Annex A carries a footnote: "Does not show the benefit of Council highway investment from 2024/25 onwards." The council also acknowledges that survey timing can delay the capture of newly resurfaced sections. If the council points to recent investment, your claim should ask whether that investment reached your specific road before your incident — and whether safety inspections caught the defect regardless.',
          },
          {
            question: 'Should I report the pothole to Herefordshire before claiming?',
            answer: 'Reporting creates a dated council record that can prove actual notice if the defect was reported before your incident. Herefordshire\'s Highways Maintenance Plan sets out how safety inspections identify and categorise defects for repair. A prior report through the council\'s online highways service or FixMyStreet — combined with photos showing the defect\'s size, depth and visible age — is often stronger evidence than network-level condition percentages alone.',
          },
          {
            question: 'Why is Best Practice AMBER if Herefordshire uses AI road surveys?',
            answer: 'Herefordshire has adopted Vaisala Road AI for unclassified roads and Gaist video surveys for footways — genuine innovation. But the DfT Best Practice scorecard is AMBER, and the council notes its Transport Asset Management Plan is "drawing to the end of its life" with policy under review. For your claim, the gap between modern survey tools and still-worsening B/C condition data is the point: knowing network condition in aggregate does not prove the specific pothole was repaired in reasonable time.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Herefordshire Council"
        councilSlug="herefordshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/herefordshire"
        headline="Herefordshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Herefordshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Herefordshire: B and C Roads in RED Have Doubled
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Herefordshire maintains <strong className="text-white">3,338km of roads</strong> across one of England&apos;s
            most rural counties — more than 80% of the network is rural — and earns{' '}
            <strong className="text-emerald-400">GREEN for Spend</strong> with £50.87m planned in 2025/26.
            Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong>: B and C roads in RED
            condition doubled from 6–8% to <strong className="text-rose-400">13%</strong> by 2024, green-condition
            local roads fell from 62% to 51%, and the council filled{' '}
            <strong className="text-rose-400">106,650 potholes in four years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">31,963</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2023/24 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Peak reactive year on a 3,338km network — roughly 88 pothole repairs every single day,
              before the council&apos;s own additional investment from July 2024 began to take effect.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and Road AI survey data from Herefordshire&apos;s own transparency report —
            A-roads slipping, B and C roads in sharp decline
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">The network (3,338km total — overwhelmingly rural)</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">383km</div>
                <div className="text-sm text-surface-700">A roads (11.5%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">1,390km</div>
                <div className="text-sm text-amber-800">B and C roads (41.6%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">1,566km</div>
                <div className="text-sm text-amber-800">Unclassified (46.9%)</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Herefordshire&apos;s own report states the network is &ldquo;predominantly rural in its nature with
              more than 80% of the network being considered rural from a highway perspective.&rdquo; There are also
              747km of footways, 40km of cycleways and in excess of 800 highway structures — with structures in
              poor or very poor condition rising from 14.92% in 2019/20 to 26.4% by 2023/24.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (383km — 11.5% of network): recent slip</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold">65%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads had been improving steadily through 2023 — then RED share jumped from 5% to 7% and green
              roads fell six points in a single year. All condition figures carry the council&apos;s footnote that
              they do not show the benefit of investment from 2024/25 onwards.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,390km — 42% of network): declining sharply</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">61%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">60%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">13%</td>
                    <td className="px-4 py-2 text-center font-bold">36%</td>
                    <td className="px-4 py-2 text-center font-bold">51%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads more than doubled from 6% to 13% between 2021 and 2024 — roughly 181km
              of local classified roads now in the worst category. Green roads fell from 62% to 51%: almost
              half the B/C network is amber or red and needs — or will soon need — maintenance. B roads are
              surveyed annually; C roads on a three-year cycle.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Condition Still AMBER</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£26.9m</div>
                <div className="text-brand-300 text-sm">DfT grant funding 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£50.9m</div>
                <div className="text-brand-300 text-sm">Total projected expenditure 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">15.7%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share (2025/26)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Herefordshire spends nearly <span className="text-emerald-400">double its DfT allocation</span> —
              £50.87m total against £26.859m in grant funding — earning a GREEN Spend scorecard. But preventative
              maintenance was just 12.1% in 2024/25, and the council admits striking the balance between reactive
              demand and prevention &ldquo;has proved difficult.&rdquo; Local roads are still deteriorating faster
              than the published survey data captures.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,566km Rural Backbone
          </h2>
          <p className="text-surface-600 mb-8">
            Nearly half the network is unclassified — surveyed with AI, but one in five in RED condition
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">14%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Road AI — Better Surveys, Same Rural Risk</h3>
              <p className="text-surface-600 text-sm mb-3">
                Herefordshire replaced four-year Course Visual Inspections with Vaisala Road AI in 2020, surveying
                50% of the U-road network annually on a two-year full cycle. The council chose this because results
                are &ldquo;more closely aligned to the actual experience of users&rdquo; — but at the 2024 survey,
                <strong> 20% of U-roads were still in RED condition</strong> — approximately 313km of village lanes,
                farm tracks and estate roads.
              </p>
              <p className="text-surface-600 text-sm">
                The council was an early adopter of jet patching on C and U-roads — innovation that helps repairs,
                but does not eliminate the defects that trigger claims.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Survey Timing Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                Herefordshire&apos;s own report warns that survey frequency can delay capturing the benefit of
                investment: a road resurfaced after a survey may not show improvement until the next cycle.
                Every published condition figure carries the footnote that it does not reflect investment from
                2024/25 onwards.
              </p>
              <p className="text-amber-800 text-sm">
                For claims on rural roads, ask whether safety inspections — not just condition surveys — should
                have identified your defect regardless of when the last Road AI pass occurred.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Herefordshire&apos;s rural network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road on the council&apos;s resilient network receiving priority treatment?</li>
              <li>• If 20% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• Did safety inspections under the Highways Maintenance Plan catch the defect before it damaged your vehicle?</li>
              <li>• Can the council point to 2024/25 investment on your road — or only aggregate spend figures?</li>
            </ul>
            <p className="text-white font-medium">
              On a county where care workers, NHS staff and rural businesses depend on minor roads with few
              alternatives, a generic &ldquo;we&apos;re investing £50m&rdquo; defence is not enough without proof
              the specific defect was managed.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            106,650 Potholes in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair at scale — and the council&apos;s own admission that prevention has proved difficult
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">27,005</td>
                  <td className="px-6 py-4 text-center">2.4%</td>
                  <td className="px-6 py-4 text-center">17.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">25,455</td>
                  <td className="px-6 py-4 text-center">2.2%</td>
                  <td className="px-6 py-4 text-center">23.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31,963</td>
                  <td className="px-6 py-4 text-center">1.9%</td>
                  <td className="px-6 py-4 text-center">21.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">22,227</td>
                  <td className="px-6 py-4 text-center">12.1%</td>
                  <td className="px-6 py-4 text-center">17.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26*</td>
                  <td className="px-6 py-4 text-center">3,994</td>
                  <td className="px-6 py-4 text-center">15.7%</td>
                  <td className="px-6 py-4 text-center">13.8%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">106,650</td>
                  <td className="px-6 py-4 text-center text-surface-400" colSpan={2}>—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-6">
            *2025/26 pothole data is year-to-date to end of May 2025. The council estimates in excess of 25,000
            pothole repairs in the full 2025/26 year. Spend percentages from Annex A; pothole counts from the
            safety inspection repair process.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~73 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over four complete years, Herefordshire fills around 73 potholes per day across its
                3,338km network. The council states around 80% of reactive investment goes on carriageway repairs,
                with the majority being pothole repairs. A network producing defects at that rate is one where
                potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Prevention Pivot — Not Yet Visible</h3>
              <p className="text-amber-800 text-sm">
                Preventative spend jumped from 1.9% in 2023/24 to 12.1% in 2024/25 and is projected at 15.7% in
                2025/26 — part of the council&apos;s move away from a reactive regime. But the council itself
                notes this balance &ldquo;has proved difficult,&rdquo; and condition data still shows B/C roads
                worsening through 2024. Investment intent and survey outcomes are not yet aligned.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Time Lag Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Time Lag Admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own explanation for why published condition data lags behind its investment
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="However, while the report outlines the scale of the maintenance and improvement activity, it does not reflect the full benefit and impact of the council's additional investment over the past two years due to a time lag in national road condition surveys."
              source="Herefordshire Council press release, 30 June 2025"
            />
            <QuoteCard
              quote="When considering the reactive works required across the county around 80% of the investment is in carriageway repairs, with the majority being the repair of potholes."
              source="Herefordshire Council Local Highways Maintenance Transparency Report — Annex A (June 2025)"
            />
            <QuoteCard
              quote="The survey frequency for each of the classifications can lead to a delay in the realisation of investment from programmes. In some cases, a survey may be done prior to a scheme being delivered in that year, the result being that the new material will be surveyed in part during the next survey and a further cycle being required to capture the full benefit of the works."
              source="Herefordshire Council Local Highways Maintenance Transparency Report — Annex A (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Herefordshire publicly acknowledges that its transparency report cannot yet demonstrate the
                impact of significant additional investment since July 2024 — including £50.87m planned for
                2025/26 with c75km of resurfacing and c100km of surface dressing.
              </p>
              <p className="text-surface-600 text-sm">
                That honesty helps residents understand the programme — but it also limits what the council
                can prove in a claim defence about network condition at the time of your incident.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Had your road received any 2024/25 resurfacing or surface dressing before your incident?</li>
                <li>• Is your road on the published summer 2025 resurfacing programme?</li>
                <li>• If condition surveys lag investment, did safety inspections still catch the defect?</li>
                <li>• Did drainage improvements — 100+ schemes planned — address water pooling at your location?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Herefordshire is investing heavily — here&apos;s how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — nearly double DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy and Highways Maintenance Plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Road AI surveys on U-roads — 50% of network annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative spend rising — 15.7% projected in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> c75km resurfacing and c100km surface dressing planned
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on recently treated A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED doubled to 13%, green roads down to 51%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 20% of 1,566km U-roads in RED at 2024 survey — ~313km of rural lanes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 106,650 potholes filled in four years — defects form faster than prevention eliminates them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits condition data does not yet show 2024/25 investment benefit
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Highway structures in poor/very poor condition up from 14.92% to 26.4%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER Best Practice — TAMP under review, asset plan nearing end of life
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a £50m programme, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — B/C decline and U-road RED percentages are your structural arguments</li>
              <li>• Whether the road appeared on the 2025 resurfacing or surface dressing programme before your incident</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Herefordshire&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Herefordshire first
          </h2>
          <p className="text-surface-600 mb-6">
            Herefordshire&apos;s Highways Maintenance Plan sets out how safety inspections identify and categorise
            defects for repair. Reporting the defect through the council creates a dated record — useful evidence
            if the pothole was reported before your incident, or if the council failed to repair it within a
            reasonable time.
          </p>
          <a
            href="https://my.herefordshire.gov.uk/en/service/Highways___Report_a_highways_issue?inputID=Pothole"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Herefordshire Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council&apos;s online reporting service or the pothole progress map. Keep your reference number
            and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Herefordshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded rural council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No B/C RED doubling cited</li>
                <li>• No time-lag survey argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C RED doubling to 13% documented</li>
                <li>✅ 1,566km U-road network and 20% RED cited</li>
                <li>✅ 106,650 pothole repairs in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Herefordshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=herefordshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Herefordshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Herefordshire earns a GREEN Spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend rating is GREEN because Herefordshire is projecting £50.87m total highway
                expenditure in 2025/26 against £26.859m DfT grant funding — nearly double. But your claim turns
                on whether the specific defect that damaged your vehicle was reasonably inspected and repaired
                under Section 58, not on aggregate spend. Condition is AMBER overall, with B and C roads in RED
                condition doubling from 6–8% to 13% by 2024.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or C road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,566km — 47% of Herefordshire&apos;s 3,338km network — and 20% were
                in RED condition at the 2024 survey. C roads are surveyed on a three-year cycle, while U-roads
                use Vaisala Road AI on a two-year cycle. On a network the council describes as more than 80%
                rural, defects on minor roads are common — and prior reports plus photos showing defect age
                matter more than headline A-road scores.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council&apos;s £50m investment mean my claim will fail?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. Herefordshire&apos;s own June 2025 press release states the transparency report
                &ldquo;does not reflect the full benefit and impact of the council&apos;s additional investment over
                the past two years due to a time lag in national road condition surveys.&rdquo; The published
                condition tables carry the same caveat. A council cannot rely on investment not yet visible in
                its own survey data to defeat a claim about a specific defect it failed to repair in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 22,227 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Herefordshire still filled 22,227 potholes in 2024/25 — about 61 every day — after a peak of
                31,963 in 2023/24. The council estimates it will repair in excess of 25,000 potholes in 2025/26
                while pursuing a preventative strategy. Around 80% of reactive investment goes on carriageway
                repairs, with the majority being pothole repairs.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the time lag in condition surveys help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Every condition table in Herefordshire&apos;s Annex A carries a footnote: &ldquo;Does not
                show the benefit of Council highway investment from 2024/25 onwards.&rdquo; The council also
                acknowledges that survey timing can delay the capture of newly resurfaced sections. If the council
                points to recent investment, your claim should ask whether that investment reached your specific
                road before your incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Should I report the pothole to Herefordshire before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Reporting creates a dated council record that can prove actual notice if the defect was reported
                before your incident. Herefordshire&apos;s Highways Maintenance Plan sets out how safety inspections
                identify and categorise defects for repair. A prior report through the council&apos;s online highways
                service or FixMyStreet — combined with photos showing the defect&apos;s size, depth and visible age
                — is often stronger evidence than network-level condition percentages alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Best Practice AMBER if Herefordshire uses AI road surveys?
              </h3>
              <p className="text-surface-600 text-sm">
                Herefordshire has adopted Vaisala Road AI for unclassified roads and Gaist video surveys for
                footways — genuine innovation. But the DfT Best Practice scorecard is AMBER, and the council notes
                its Transport Asset Management Plan is &ldquo;drawing to the end of its life&rdquo; with policy under
                review. For your claim, the gap between modern survey tools and still-worsening B/C condition data
                is the point: knowing network condition in aggregate does not prove the specific pothole was
                repaired in reasonable time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.herefordshire.gov.uk/travel-and-transport/roads/herefordshire-road-investment/additional-road-investment-2019-to-2026/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Herefordshire Council Local Highways Maintenance Transparency Report — Annex A and Annex B (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
